/**
 * SigNoz APM Telemetry Service
 *
 * Provides browser-based OpenTelemetry instrumentation for:
 * - User interactions (clicks, inputs, form submissions)
 * - Network requests (fetch/XHR)
 * - Custom spans and events
 *
 * Based on official SigNoz frontend monitoring guide:
 * @see https://signoz.io/docs/userguide/send-traces-to-signoz/
 */

import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { resourceFromAttributes } from '@opentelemetry/resources'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request'
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction'
import { ZoneContextManager } from '@opentelemetry/context-zone'
import { trace, SpanStatusCode } from '@opentelemetry/api'

/**
 * Telemetry configuration options
 */
interface TelemetryConfig {
    /** Service name (appears in SigNoz UI) */
    serviceName: string
    /** SigNoz ingestion endpoint */
    endpoint: string
    /** SigNoz ingestion key */
    ingestionKey: string
    /** Enable/disable telemetry */
    enabled: boolean
    /** Environment (dev, staging, prod) */
    environment: string
    /** SigNoz region (us, eu, in) */
    region?: string
}

/**
 * Custom span attributes for frontend-specific context
 */
interface CustomSpanAttributes {
    /** Current route path */
    'app.route'?: string
    /** User ID (if authenticated) */
    'user.id'?: string
    /** Organization ID */
    'organization.id'?: string
    /** Component name */
    'component.name'?: string
    /** Custom metadata */
    [key: string]: string | number | boolean | undefined
}

class TelemetryService {
    private provider: WebTracerProvider | null = null
    private tracer: ReturnType<typeof trace.getTracer> | null = null
    private config: TelemetryConfig | null = null
    private initialized = false

    /**
     * Initialize OpenTelemetry instrumentation
     */
    initialize(config: TelemetryConfig): void {
        if (this.initialized) {
            console.warn('[Telemetry] Already initialized')
            return
        }

        if (!config.enabled) {
            console.info('[Telemetry] Disabled via configuration')
            return
        }

        this.config = config

        try {
            // Create resource with service metadata
            const resource = resourceFromAttributes({
                'service.name': config.serviceName,
                'service.version': import.meta.env.VITE_APP_VERSION || 'unknown',
                'deployment.environment': config.environment,
                // Custom frontend attributes
                'app.framework': 'vue3',
                'app.runtime': 'browser'
            })

            // Configure OTLP exporter
            const exporter = new OTLPTraceExporter({
                url: this.buildEndpointUrl(config),
                headers: {
                    'signoz-ingestion-key': config.ingestionKey
                }
            })

            // Create tracer provider with batch processor
            this.provider = new WebTracerProvider({
                resource,
                spanProcessors: [new BatchSpanProcessor(exporter)]
            })

            // Register the provider with Zone context manager for async context propagation
            this.provider.register({
                contextManager: new ZoneContextManager()
            })

            // Get tracer instance
            this.tracer = trace.getTracer(config.serviceName)

            // Register auto-instrumentations
            this.registerInstrumentations()

            this.initialized = true

            console.info(`[Telemetry] Initialized for ${config.serviceName}`, {
                endpoint: this.buildEndpointUrl(config),
                environment: config.environment
            })
        } catch (error) {
            console.error('[Telemetry] Initialization failed:', error)
        }
    }

    /**
     * Build SigNoz endpoint URL
     */
    private buildEndpointUrl(config: TelemetryConfig): string {
        // If custom endpoint provided, use it
        if (config.endpoint) {
            return config.endpoint
        }

        // Build SigNoz Cloud endpoint
        const region = config.region || 'us'
        return `https://ingest.${region}.signoz.cloud:443/v1/traces`
    }

    /**
     * Register browser auto-instrumentations
     */
    private registerInstrumentations(): void {
        registerInstrumentations({
            instrumentations: [
                // Instrument fetch API
                new FetchInstrumentation({
                    // Ignore SigNoz endpoints to prevent infinite loop
                    ignoreUrls: [/signoz\.cloud/],
                    // Propagate trace headers to backend for distributed tracing
                    // Backend must accept 'traceparent' and 'tracestate' headers in CORS config
                    propagateTraceHeaderCorsUrls: [
                        /api\.lantern\.cirrusinvicta\.com/,
                        /api-staging\.lantern\.cirrusinvicta\.com/,
                        /api-testing\.lantern\.cirrusinvicta\.com/
                    ],
                    // Add custom attributes to fetch spans
                    applyCustomAttributesOnSpan: (span, request) => {
                        if (request instanceof Request) {
                            span.setAttribute('http.url', request.url)
                        }
                    }
                }),

                // Instrument XMLHttpRequest (for axios fallback)
                new XMLHttpRequestInstrumentation({
                    ignoreUrls: [/signoz\.cloud/],
                    // Propagate trace headers to backend (same domains as fetch)
                    propagateTraceHeaderCorsUrls: [
                        /api\.lantern\.cirrusinvicta\.com/,
                        /api-staging\.lantern\.cirrusinvicta\.com/,
                        /api-testing\.lantern\.cirrusinvicta\.com/
                    ]
                }), // Instrument user interactions (clicks, inputs, form submissions)
                new UserInteractionInstrumentation({
                    eventNames: ['click', 'input', 'submit']
                })
            ]
        })
    }

    /**
     * Create a custom span for tracking operations
     *
     * @example
     * await telemetryService.traceOperation('api.fetchDomains', async (span) => {
     *   span.setAttribute('organization.id', '123')
     *   return await DomainService.getDomains()
     * })
     */
    async traceOperation<T>(
        operationName: string,
        operation: (span: ReturnType<typeof trace.getActiveSpan>) => Promise<T>,
        attributes?: CustomSpanAttributes
    ): Promise<T> {
        if (!this.tracer) {
            // If telemetry not initialized, just run operation without span
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return operation(undefined as any)
        }

        return this.tracer.startActiveSpan(operationName, async (span) => {
            try {
                // Add custom attributes
                if (attributes) {
                    Object.entries(attributes).forEach(([key, value]) => {
                        if (value !== undefined) {
                            span.setAttribute(key, value)
                        }
                    })
                }

                // Execute operation
                const result = await operation(span)

                // Mark span as successful
                span.setStatus({ code: SpanStatusCode.OK })

                return result
            } catch (error) {
                // Record error in span
                span.setStatus({
                    code: SpanStatusCode.ERROR,
                    message: error instanceof Error ? error.message : 'Unknown error'
                })

                if (error instanceof Error) {
                    span.recordException(error)
                }

                throw error
            } finally {
                span.end()
            }
        })
    }

    /**
     * Record a custom event (for important user actions)
     *
     * @example
     * telemetryService.recordEvent('domain.created', {
     *   'domain.name': 'example.com',
     *   'organization.id': '123'
     * })
     */
    recordEvent(eventName: string, attributes?: CustomSpanAttributes): void {
        if (!this.tracer) return

        const span = trace.getActiveSpan()
        if (span) {
            span.addEvent(eventName, attributes)
        } else {
            // Create a short-lived span for the event
            this.tracer.startActiveSpan(eventName, (span) => {
                if (attributes) {
                    Object.entries(attributes).forEach(([key, value]) => {
                        if (value !== undefined) {
                            span.setAttribute(key, value)
                        }
                    })
                }
                span.end()
            })
        }
    }

    /**
     * Set user context for all future traces
     *
     * @example
     * telemetryService.setUser({ id: '123', email: 'user@example.com' })
     */
    setUser(user: { id: string; email?: string; organizationId?: string }): void {
        if (!this.tracer) return

        // Set user attributes on the active span
        const span = trace.getActiveSpan()
        if (span) {
            span.setAttribute('user.id', user.id)
            if (user.email) {
                span.setAttribute('user.email', user.email)
            }
            if (user.organizationId) {
                span.setAttribute('organization.id', user.organizationId)
            }
        }
    }

    /**
     * Flush all pending spans before page unload
     */
    async flush(): Promise<void> {
        if (!this.provider) return

        try {
            await this.provider.forceFlush()
            console.debug('[Telemetry] Flushed pending spans')
        } catch (error) {
            console.error('[Telemetry] Failed to flush spans:', error)
        }
    }

    /**
     * Shutdown telemetry (cleanup)
     */
    async shutdown(): Promise<void> {
        if (!this.provider) return

        try {
            await this.provider.shutdown()
            this.initialized = false
            console.info('[Telemetry] Shutdown complete')
        } catch (error) {
            console.error('[Telemetry] Shutdown failed:', error)
        }
    }

    /**
     * Check if telemetry is active
     */
    get isEnabled(): boolean {
        return this.initialized && this.config?.enabled === true
    }
}

// Export singleton instance
export const telemetryService = new TelemetryService()

// Export types
export type { TelemetryConfig, CustomSpanAttributes }
